/** Performance Digest slice — the reference dgModel() (design-reference script lines 1767–1872), ported verbatim.
 *  Settings toggles, weekly/monthly/history tabs, generation (setTimeout → c.timers.after), KPI grid, daily P&L,
 *  highlights, symbol breakdown, AI summary and archive. Period data comes from c.data.digest; the ledger from c.trades(). */
import type { Ctx } from './ctx';
import type { ModelState } from './state';
import type { ViewModel } from '@/screens/generated/view-model';

type DgTogKey = 'dgWeekly' | 'dgMonthly' | 'dgAi' | 'dgLog' | 'dgEquity';

export function buildDigest(c: Ctx) {
  const S = c.S;
  const D = c.data.digest;
  const money = (v: number) => (v < 0 ? '−$' : '$') + Math.abs(v).toFixed(2);
  const sgn = (v: number) => (v >= 0 ? '+$' : '−$') + Math.abs(v).toFixed(2);
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dayLabel = (off: number) => {
    const d = new Date(Date.UTC(2026, 6, 24) - off * 86400000);
    return M[d.getUTCMonth()] + ' ' + d.getUTCDate();
  };

  const span = S.dgTab === 'monthly' ? 30 : 6;
  const rows = c.trades().filter((r) => r[1] <= span);
  const wins = rows.filter((r) => r[8] > 0),
    losses = rows.filter((r) => r[8] < 0);
  const grossWin = wins.reduce((s, r) => s + r[8], 0);
  const grossLoss = Math.abs(losses.reduce((s, r) => s + r[8], 0));
  const net = grossWin - grossLoss;
  const byDay: Record<number, number> = {};
  rows.forEach((r) => {
    byDay[r[1]] = (byDay[r[1]] || 0) + r[8];
  });
  const dayPnl = (k: number) => byDay[k] ?? 0;
  const dayKeys = Object.keys(byDay)
    .map(Number)
    .sort((a, b) => b - a);
  const peak = Math.max(1, ...dayKeys.map((k) => Math.abs(dayPnl(k))));
  const best = rows.slice().sort((a, b) => b[8] - a[8])[0];
  const worst = rows.slice().sort((a, b) => a[8] - b[8])[0];
  const symMap: Record<string, { n: number; pnl: number }> = {};
  rows.forEach((r) => {
    const e = (symMap[r[3]] = symMap[r[3]] || { n: 0, pnl: 0 });
    e.n++;
    e.pnl += r[8];
  });
  const sym = (k: string) => symMap[k] ?? { n: 0, pnl: 0 };

  const tog = (key: DgTogKey) => ({
    on: S[key],
    go: () =>
      c.set((s) => {
        const patch: Partial<ModelState> = {};
        patch[key] = !s[key];
        return patch;
      }),
    track: S[key] ? 'color-mix(in srgb, var(--pos) 55%, transparent)' : 'var(--l10)',
    knob: S[key] ? 'translateX(14px)' : 'translateX(0)',
    knobBg: S[key] ? 'var(--pos)' : 'var(--muted2)',
  });

  const made = S.dgMade[S.dgTab];
  const periodLabel = S.dgTab === 'monthly' ? 'Monthly' : 'Weekly';

  return {
    dgTabs: (
      [
        ['weekly', 'Weekly'],
        ['monthly', 'Monthly'],
        ['history', 'History'],
      ] as const
    ).map(([id, label]) => ({
      label,
      go: () => c.set({ dgTab: id }),
      bg: S.dgTab === id ? 'var(--rail)' : 'transparent',
      fg: S.dgTab === id ? 'var(--rail-ink)' : 'var(--muted)',
      fw: S.dgTab === id ? '600' : '500',
    })),
    dgIsHistory: S.dgTab === 'history',
    // dgIsReport (reference) omitted — never bound by the template, absent from the generated ViewModel
    dgWeeklyTog: tog('dgWeekly'),
    dgMonthlyTog: tog('dgMonthly'),
    dgAiTog: tog('dgAi'),
    dgLogTog: tog('dgLog'),
    dgEquityTog: tog('dgEquity'),
    dgDayOpts: DAYS.map((d, i) => ({
      label: d.slice(0, 3),
      on: S.dgDay === i,
      go: () => c.set({ dgDay: i }),
      bg: S.dgDay === i ? 'var(--rail)' : 'transparent',
      fg: S.dgDay === i ? 'var(--rail-ink)' : 'var(--muted)',
    })),
    // dgDayLabel (reference) omitted — never bound by the template, absent from the generated ViewModel
    dgGenLabel: S.dgBusy ? 'Generating…' : 'Generate ' + periodLabel + ' Digest',
    dgGen: () => {
      if (S.dgBusy) return;
      c.set({ dgBusy: true });
      c.timers.after(900, () => {
        c.set((s) => ({ dgBusy: false, dgMade: Object.assign({}, s.dgMade, { [s.dgTab]: true }) }));
        c.flash(periodLabel + ' digest generated', 'pos');
      });
    },
    dgBusy: S.dgBusy,
    dgMade: made && !S.dgBusy,
    dgUnmade: !made && !S.dgBusy,
    dgEmptyMsg: 'Click "Generate ' + periodLabel + ' Digest" to preview your report',
    dgBusyMsg: 'Generating your ' + periodLabel.toLowerCase() + ' performance digest…',
    dgPeriodTitle: periodLabel + ' Performance Digest',
    dgRange: dayLabel(span) + ' — ' + dayLabel(0) + ', 2026',
    dgNet: sgn(net),
    dgNetFg: net >= 0 ? 'var(--pos)' : 'var(--neg)',
    dgKpis: [
      { icon: 'ph-target', label: 'Total Trades', v: String(rows.length), c: 'var(--text)' },
      {
        icon: 'ph-trophy',
        label: 'Win Rate',
        v: (rows.length ? (wins.length / rows.length) * 100 : 0).toFixed(1) + '%',
        c: 'var(--text)',
      },
      {
        icon: 'ph-trend-up',
        label: 'Avg Win',
        v: money(wins.length ? grossWin / wins.length : 0),
        c: 'var(--pos)',
      },
      {
        icon: 'ph-trend-down',
        label: 'Avg Loss',
        v: losses.length ? '−$' + (grossLoss / losses.length).toFixed(2) : '$0.00',
        c: 'var(--neg)',
      },
      { icon: 'ph-calendar-blank', label: 'Trading Days', v: String(dayKeys.length), c: 'var(--text)' },
      {
        icon: 'ph-trend-up',
        label: 'Winning Days',
        v: String(dayKeys.filter((k) => dayPnl(k) > 0).length),
        c: 'var(--pos)',
      },
      {
        icon: 'ph-trend-down',
        label: 'Losing Days',
        v: String(dayKeys.filter((k) => dayPnl(k) < 0).length),
        c: 'var(--neg)',
      },
      { icon: 'ph-chart-bar', label: 'Balance', v: D.balance, c: 'var(--text)' },
    ],
    dgDaily: dayKeys
      .slice()
      .sort((a, b) => b - a)
      .map((k) => ({
        label: dayLabel(k),
        h: ((Math.abs(dayPnl(k)) / peak) * 100).toFixed(1) + '%',
        pos: dayPnl(k) >= 0,
        neg: dayPnl(k) < 0,
      })),
    dgHasDaily: dayKeys.length > 0,
    dgBest: best ? { sym: best[3], pnl: sgn(best[8]) } : undefined,
    dgWorst: worst ? { sym: worst[3], pnl: money(worst[8]) } : undefined,
    dgHasBest: !!best,
    dgHasWorst: !!worst,
    dgSymbols: Object.keys(symMap)
      .sort((a, b) => sym(b).pnl - sym(a).pnl)
      .slice(0, 6)
      .map((k) => ({
        sym: k,
        n: sym(k).n === 1 ? '1 trade' : sym(k).n + ' trades',
        pnl: money(sym(k).pnl),
        pnlFg: sym(k).pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
      })),
    dgNoSymbols: Object.keys(symMap).length === 0,
    dgAiParas: [
      periodLabel +
        ' net came in at ' +
        sgn(net) +
        ' across ' +
        rows.length +
        ' trades and ' +
        dayKeys.length +
        ' trading days, at a ' +
        (rows.length ? ((wins.length / rows.length) * 100).toFixed(0) : 0) +
        '% win rate. ' +
        (best ? best[3] + ' carried the period at ' + sgn(best[8]) + '.' : ''),
      D.aiOutlook,
    ].map((t) => ({ t })),
    dgSend: () => c.flash(periodLabel + ' digest forwarded to your investors', 'pos'),
    dgArchive: () => c.flash(periodLabel + ' digest saved to the archive', 'pos'),
    dgHistory: [
      {
        period: D.current.period,
        range: D.current.range,
        pnl: sgn(net),
        pnlFg: net >= 0 ? 'var(--pos)' : 'var(--neg)',
        trades: rows.length + ' trades',
        summary: D.current.summary,
      },
      ...D.archive.map((h) => ({
        period: h.period,
        range: h.range,
        pnl: h.pnl,
        pnlFg: h.pnlFg,
        trades: h.trades,
        summary: h.summary,
      })),
    ],
  } satisfies Partial<ViewModel>;
}
export type DigestModel = ReturnType<typeof buildDigest>;
